import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getPermissionsByService,
  getAvailableResources,
  getAvailableActions,
} from '../api/permissions';

/**
 * Loads service / resource / action option lists for permission filter UIs.
 * Resources cascade off selected services; actions cascade off both.
 */
export default function usePermissionFilterOptions({
  selectedServices = [],
  selectedResources = [],
  enabled = true,
} = {}) {
  const {
    data: permissionsByService = {},
    isLoading: isServicesLoading,
  } = useQuery(['permissions-by-service'], getPermissionsByService, {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 1,
    enabled,
  });

  const services = useMemo(
    () => Object.keys(permissionsByService).sort(),
    [permissionsByService]
  );

  const servicesKey = useMemo(() => [...selectedServices].sort().join(','), [selectedServices]);
  const resourcesKey = useMemo(() => [...selectedResources].sort().join(','), [selectedResources]);

  const {
    data: resources = [],
    isLoading: isResourcesLoading,
  } = useQuery(
    ['available-resources', servicesKey],
    () => getAvailableResources(selectedServices.length > 0 ? selectedServices : null, { isCommon: '1' }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
      enabled,
    }
  );

  const {
    data: actions = [],
    isLoading: isActionsLoading,
  } = useQuery(
    ['available-actions', servicesKey, resourcesKey],
    () =>
      getAvailableActions({
        isCommon: '1',
        ...(selectedServices.length > 0 ? { service: selectedServices } : {}),
        ...(selectedResources.length > 0 ? { resource: selectedResources } : {}),
      }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
      enabled,
    }
  );

  return {
    services,
    resources,
    actions,
    isServicesLoading,
    isResourcesLoading,
    isActionsLoading,
  };
}
